import React, { useState, useEffect, useMemo } from 'react';
//@ts-ignore
import { ComposableMap, Geographies, Geography, ZoomableGroup, Line, Point } from 'react-simple-maps';
//@ts-ignore
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
//@ts-ignore
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

// World map data - using a more reliable source
const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

interface TimeZoneMapProps {
  currentTime: Date;
  selectedTimeZone: string;
  onTimeZoneSelect: (timeZone: string) => void;
}

interface TimeZoneRegion {
  timeZone: string;
  countries: string[];
  color: string;
}

// Define time zone regions with their associated countries
const timeZoneRegions: TimeZoneRegion[] = [
  {
    timeZone: 'UTC',
    countries: ['Greenland', 'Iceland', 'Ivory Coast', 'Ghana', 'Gambia', 'Guinea', 'Mali', 'Mauritania', 'Senegal', 'Sierra Leone', 'Togo'],
    color: '#94A3B8' // Slate-400
  },
  {
    timeZone: 'Europe/London',
    countries: ['United Kingdom', 'Ireland', 'Portugal', 'Faroe Islands', 'Gibraltar'],
    color: '#60A5FA' // Blue-400
  },
  {
    timeZone: 'Europe/Paris',
    countries: ['France', 'Germany', 'Italy', 'Spain', 'Netherlands', 'Belgium', 'Switzerland', 'Austria', 'Sweden', 'Norway', 'Denmark', 'Poland', 'Czech Republic', 'Slovakia', 'Hungary', 'Romania', 'Bulgaria', 'Greece', 'Croatia', 'Slovenia', 'Serbia', 'Montenegro', 'North Macedonia', 'Albania', 'Bosnia and Herzegovina', 'Kosovo'],
    color: '#3B82F6' // Blue-500
  },
  {
    timeZone: 'Europe/Moscow',
    countries: ['Russia', 'Belarus', 'Ukraine', 'Moldova', 'Armenia', 'Azerbaijan', 'Georgia'],
    color: '#1D4ED8' // Blue-700
  },
  {
    timeZone: 'Asia/Kolkata',
    countries: ['India', 'Sri Lanka', 'Nepal', 'Bhutan'],
    color: '#F59E0B' // Amber-500
  },
  {
    timeZone: 'Asia/Shanghai',
    countries: ['China', 'Hong Kong', 'Taiwan', 'Singapore', 'Malaysia', 'Philippines', 'Indonesia', 'Vietnam', 'Thailand', 'Cambodia', 'Laos', 'Myanmar', 'Brunei'],
    color: '#D97706' // Amber-600
  },
  {
    timeZone: 'Asia/Tokyo',
    countries: ['Japan', 'South Korea', 'North Korea'],
    color: '#B45309' // Amber-700
  },
  {
    timeZone: 'Asia/Dubai',
    countries: ['United Arab Emirates', 'Saudi Arabia', 'Qatar', 'Bahrain', 'Kuwait', 'Oman', 'Yemen', 'Iraq', 'Iran', 'Afghanistan', 'Pakistan'],
    color: '#F97316' // Orange-500
  },
  {
    timeZone: 'Africa/Cairo',
    countries: ['Egypt', 'Sudan', 'Libya', 'Tunisia', 'Algeria', 'Morocco', 'Western Sahara'],
    color: '#EA580C' // Orange-600
  },
  {
    timeZone: 'Africa/Johannesburg',
    countries: ['South Africa', 'Namibia', 'Botswana', 'Zimbabwe', 'Zambia', 'Malawi', 'Mozambique', 'Eswatini', 'Lesotho'],
    color: '#C2410C' // Orange-700
  },
  {
    timeZone: 'America/New_York',
    countries: ['United States of America', 'Canada', 'Bahamas', 'Jamaica', 'Haiti', 'Dominican Republic', 'Puerto Rico', 'U.S. Virgin Islands', 'Turks and Caicos Islands', 'British Virgin Islands', 'Anguilla', 'Montserrat', 'Guadeloupe', 'Martinique', 'Saint Barthélemy', 'Saint Martin', 'Grenada', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Barbados', 'Trinidad and Tobago', 'Guyana', 'Suriname', 'French Guiana'],
    color: '#10B981' // Emerald-500
  },
  {
    timeZone: 'America/Chicago',
    countries: ['United States of America', 'Canada', 'Mexico', 'Belize', 'Guatemala', 'El Salvador', 'Honduras', 'Nicaragua', 'Costa Rica', 'Panama', 'Colombia', 'Ecuador', 'Peru'],
    color: '#059669' // Emerald-600
  },
  {
    timeZone: 'America/Los_Angeles',
    countries: ['United States of America', 'Canada', 'Mexico'],
    color: '#047857' // Emerald-700
  },
  {
    timeZone: 'America/Anchorage',
    countries: ['United States of America'],
    color: '#065F46' // Emerald-800
  },
  {
    timeZone: 'Pacific/Auckland',
    countries: ['New Zealand', 'Fiji', 'Tonga', 'Samoa', 'Cook Islands', 'French Polynesia', 'New Caledonia', 'Vanuatu', 'Solomon Islands'],
    color: '#8B5CF6' // Violet-500
  },
  {
    timeZone: 'Australia/Sydney',
    countries: ['Australia', 'Papua New Guinea', 'Solomon Islands', 'Vanuatu'],
    color: '#7C3AED' // Violet-600
  },
  {
    timeZone: 'Pacific/Honolulu',
    countries: ['United States of America'],
    color: '#6D28D9' // Violet-700
  }
];

// Define time zone boundary lines
const timeZoneBoundaries = [
  { longitude: -180 },
  { longitude: -150 },
  { longitude: -120 },
  { longitude: -90 },
  { longitude: -60 },
  { longitude: -30 },
  { longitude: 0 },
  { longitude: 30 },
  { longitude: 60 },
  { longitude: 90 },
  { longitude: 120 },
  { longitude: 150 },
  { longitude: 180 }
];

const TimeZoneMap: React.FC<TimeZoneMapProps> = ({ currentTime, selectedTimeZone, onTimeZoneSelect }) => {
  const [tooltipContent, setTooltipContent] = useState<string>('');
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [mapData, setMapData] = useState<any>(null);
  const [isLegendExpanded, setIsLegendExpanded] = useState<boolean>(false);

  // Calculate the coordinates for time zone lines
  const timeZoneLines = useMemo(() => {
    return timeZoneBoundaries.map(({ longitude }) => {
      // Create a line from north to south pole
      const coordinates: Point[] = [
        [longitude, 90] as Point, // North pole
        [longitude, -90] as Point // South pole
      ];
      
      return {
        coordinates
      };
    });
  }, []);

  useEffect(() => {
    const fetchMapData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetch(geoUrl);
        if (!response.ok) {
          throw new Error('Failed to load map data');
        }
        const data = await response.json();
        setMapData(data);
      } catch (err) {
        console.error('Error loading map data:', err);
        setError('Failed to load map data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMapData();
  }, []);

  const getTimeZoneForCountry = (countryName: string): string => {
    const region = timeZoneRegions.find(region => 
      region.countries.includes(countryName)
    );
    return region?.timeZone || 'UTC';
  };

  const getRegionColor = (countryName: string): string => {
    const region = timeZoneRegions.find(region => 
      region.countries.includes(countryName)
    );
    return region?.color || '#4B5563';
  };

  const getTimeInTimeZone = (timeZone: string): string => {
    try {
      const date = new Date();
      
      // Use Intl.DateTimeFormat to get the time in the target timezone
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZoneName: 'short'
      });

      // Get the formatted parts
      const parts = formatter.formatToParts(date);
      const timePart = parts
        .filter(part => part.type !== 'timeZoneName')
        .map(part => part.value)
        .join('');

      // Log for debugging
      console.log(`Timezone: ${timeZone}, Formatted Time: ${timePart}, Timezone Name: ${parts.find(p => p.type === 'timeZoneName')?.value}`);
      
      return timePart;
    } catch (error) {
      console.error('Error getting time for timezone:', timeZone, error);
      return 'Invalid timezone';
    }
  };

  const getTimeZoneOffset = (timeZone: string): string => {
    try {
      const date = new Date();
      
      // Use Intl.DateTimeFormat to get the timezone offset
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone,
        timeZoneName: 'longOffset'
      });

      const parts = formatter.formatToParts(date);
      const offsetPart = parts.find(part => part.type === 'timeZoneName')?.value || 'UTC±00:00';
      
      // Log for debugging
      console.log(`Timezone: ${timeZone}, Offset: ${offsetPart}, UTC: ${date.toUTCString()}`);
      
      return offsetPart;
    } catch (error) {
      console.error('Error getting offset for timezone:', timeZone, error);
      return 'UTC±00:00';
    }
  };

  const handleCountryClick = (geo: any) => {
    if (!geo.properties || !geo.properties.name) {
      console.error('No country name found in properties:', geo.properties);
      return;
    }
    const timeZone = getTimeZoneForCountry(geo.properties.name);
    onTimeZoneSelect(timeZone);
  };

  const handleCountryHover = (geo: any) => {
    // Check if properties exist
    if (!geo.properties || !geo.properties.name) {
      console.error('No country name found in properties:', geo.properties);
      return;
    }

    const countryName = geo.properties.name;
    const timeZone = getTimeZoneForCountry(countryName);
    const localTime = getTimeInTimeZone(timeZone);
    const utcOffset = getTimeZoneOffset(timeZone);
    
    // Get the timezone name in a more readable format
    const tzName = timeZone.split('/').pop()?.replace('_', ' ') || timeZone;
    
    // Log the full information for debugging
    console.log(`Country: ${countryName}, Timezone: ${timeZone}, Local Time: ${localTime}, Offset: ${utcOffset}`);
    
    setTooltipContent(
      `${countryName}\n` +
      `${tzName}\n` +
      `Local Time: ${localTime}\n` +
      `Offset: ${utcOffset}`
    );
    setHoveredRegion(countryName);
  };

  if (isLoading) {
    return (
      <div className="w-full h-[600px] bg-white dark:bg-gray-800 rounded-lg shadow-lg flex items-center justify-center">
        <div className="text-gray-600 dark:text-gray-300">Loading map...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-[600px] bg-white dark:bg-gray-800 rounded-lg shadow-lg flex items-center justify-center">
        <div className="text-red-500 dark:text-red-400">{error}</div>
      </div>
    );
  }

  if (!mapData) {
    return (
      <div className="w-full h-[600px] bg-white dark:bg-gray-800 rounded-lg shadow-lg flex items-center justify-center">
        <div className="text-gray-600 dark:text-gray-300">No map data available</div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-[600px] bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
      {/* Add disclaimer */}
      <div className="absolute top-2 left-2 right-2 z-10 bg-white/90 dark:bg-gray-800/90 px-3 py-1.5 rounded text-xs text-gray-600 dark:text-gray-300 text-center">
        This map shows time zones and is for informational purposes only. Country boundaries shown are for reference and do not represent any political position.
      </div>

      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          scale: 147,
          rotate: [0, 0, 0]
        }}
        style={{
          width: '100%',
          height: '100%'
        }}
      >
        <ZoomableGroup
          center={[0, 0]}
          zoom={1.2}
          minZoom={0.5}
          maxZoom={4}
        >
          {/* Add time zone boundary lines */}
          {timeZoneLines.map(({ coordinates }, index) => (
            <Line
              key={`timezone-line-${index}`}
              coordinates={coordinates}
              stroke="#94A3B8"
              strokeWidth={1}
              strokeDasharray="4,4"
              style={{
                pointerEvents: 'none'
              }}
            />
          ))}

          <Geographies geography={mapData}>
            {/*@ts-ignore*/}
            {({ geographies }) =>
            //@ts-ignore
              geographies.map(geo => {
                if (!geo.properties || !geo.properties.name) {
                  console.warn('No country name found for:', geo.properties);
                  return null;
                }

                const countryName = geo.properties.name;
                const isSelected = getTimeZoneForCountry(countryName) === selectedTimeZone;
                const isHovered = countryName === hoveredRegion;
                
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={getRegionColor(countryName)}
                    stroke="#1F2937"
                    strokeWidth={0.5}
                    style={{
                      default: {
                        outline: 'none',
                        opacity: isSelected ? 1 : 0.7,
                        transition: 'all 0.3s ease'
                      },
                      hover: {
                        outline: 'none',
                        opacity: 1,
                        fill: '#60A5FA',
                        cursor: 'pointer'
                      },
                      pressed: {
                        outline: 'none',
                        opacity: 0.8
                      }
                    }}
                    onClick={() => handleCountryClick(geo)}
                    onMouseEnter={() => handleCountryHover(geo)}
                    onMouseLeave={() => {
                      setTooltipContent('');
                      setHoveredRegion(null);
                    }}
                    data-tooltip-id="timezone-tooltip"
                    data-tooltip-content={tooltipContent}
                  />
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>
      
      <Tooltip
        id="timezone-tooltip"
        place="top"
        className="z-50"
        style={{
          backgroundColor: '#1F2937',
          color: '#F9FAFB',
          padding: '8px 12px',
          borderRadius: '6px',
          fontSize: '14px',
          whiteSpace: 'pre-line'
        }}
      />

      <div className="absolute bottom-4 left-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg transition-all duration-300 ease-in-out"
           style={{ 
             maxWidth: isLegendExpanded ? '300px' : '40px',
             maxHeight: isLegendExpanded ? '400px' : '40px',
             overflow: 'hidden'
           }}>
        <button
          onClick={() => setIsLegendExpanded(!isLegendExpanded)}
          className="w-full p-2 flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-t-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
        >
          <span className={`text-sm font-medium text-gray-900 dark:text-white ${!isLegendExpanded && 'hidden'}`}>
            Legend
          </span>
          {isLegendExpanded ? (
            //@ts-ignore
            <FaChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          ) : (
            //@ts-ignore
            <FaChevronUp className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          )}
        </button>
        
        <div className={`transition-all duration-300 ease-in-out ${isLegendExpanded ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-3 space-y-3 max-h-[350px] overflow-y-auto">
            <div>
              <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Time Zones</h4>
              <div className="grid grid-cols-2 gap-1.5">
                {timeZoneRegions.map((region) => (
                  <div key={region.timeZone} className="flex items-center space-x-1.5">
                    <div 
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                      style={{ backgroundColor: region.color }}
                    />
                    <span className="text-xs text-gray-600 dark:text-gray-300 truncate">
                      {region.timeZone.split('/')[1]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Time Zone Boundaries</h4>
              <div className="grid grid-cols-2 gap-1.5">
                {timeZoneBoundaries.map(({ longitude }) => (
                  <div key={longitude} className="flex items-center space-x-1.5">
                    <div 
                      className="w-2.5 h-0.5 flex-shrink-0" 
                    />
                    <span className="text-xs text-gray-600 dark:text-gray-300 truncate">
                      {longitude === -180 ? 'International Date Line' : longitude === -150 ? 'UTC-10' : longitude === -120 ? 'UTC-8' : longitude === -90 ? 'UTC-6' : longitude === -60 ? 'UTC-4' : longitude === -30 ? 'UTC-2' : longitude === 0 ? 'Prime Meridian' : longitude === 30 ? 'UTC+2' : longitude === 60 ? 'UTC+4' : longitude === 90 ? 'UTC+6' : longitude === 120 ? 'UTC+8' : longitude === 150 ? 'UTC+10' : 'International Date Line'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeZoneMap; 